// Mounted once in the root route: polls /app-config, sends the app to /maintenance while it is on — or while a 503
// from any other request says so (errorHooks.hold) — and back where it was when it ends.
import { type QueryClient, useQuery } from '@tanstack/react-query'
import { useRouter, useRouterState } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { errorHooks } from '../errors/routeError'
import { appConfigKey, appConfigQuery, holdTarget, MAINTENANCE_PATH, maintenanceOf } from './appConfig'

export function AppConfigHold({ queryClient }: { queryClient: QueryClient }) {
  const query = useQuery(appConfigQuery, queryClient)
  const router = useRouter()
  const location = useRouterState({ select: (s) => s.location })
  const [heldSince, setHeldSince] = useState<number | null>(null)

  useEffect(() => {
    const previous = errorHooks.hold
    errorHooks.hold = (hold) => {
      if (hold !== 'maintenance') return // the web has no minimum version; a stale bundle reloads itself
      setHeldSince(Date.now())
      void queryClient.invalidateQueries({ queryKey: appConfigKey })
    }
    return () => {
      errorHooks.hold = previous
    }
  }, [queryClient])

  const search = location.search as { from?: unknown }
  const target = holdTarget({
    maintenance: maintenanceOf(query),
    answeredAt: Math.max(query.dataUpdatedAt, query.errorUpdatedAt),
    heldSince,
    href: location.href,
    pathname: location.pathname,
    from: location.pathname === MAINTENANCE_PATH && typeof search.from === 'string' ? search.from : undefined,
  })

  useEffect(() => {
    if (target) void router.navigate({ href: target, replace: true })
  }, [target, router])

  return null
}
